import React from 'react';
import PropTypes from 'prop-types';
import { get, compact, forEach, startCase, reject, isBoolean } from 'lodash';

import AddedByCell from './cells/added_by';
import WaitlistStatusCell from './cells/waitlist_status_cell';
import DocAccessLevelSelectCell from './cells/doc_access_level';
import DocumentProgress from './cells/document_progress';
import { getTaskTypeLabel } from 'helpers/tasks/helpers';
import DateWithHover from 'components/timestamps/date_with_hover';
import InfoTableTooltip from 'components/tooltip/info_table_tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faSignature, faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { faSignatureSlash } from '@fortawesome/pro-solid-svg-icons';
import formatPhoneNumber from 'helpers/formatters/advanced_phone_number_formats';

// column keys
export const COMPANY = 'company';
export const NAME = 'displayName';
export const EMAIL = 'email';
export const ADDED_BY = 'addedBy';
export const DOC_ACCESS_LEVEL_FOR_SALE = 'docAccessLevelForSale';
export const DOC_ACCESS_LEVEL_FOR_LEASE = 'docAccessLevelForLease';
export const LINK_SENT = 'linkSentAt';
export const PHONE = 'phone';
export const ROLE_JOB_TITLE = 'roleJobTitle';
export const ACCOUNT_STATUS = 'account_status';
export const CA_STATUS = 'caStatus';
export const PROPERTY = 'propertyName';
export const WAITLIST_STATUS = 'waitlistStatusLabel';
export const LEAD_STATUS = 'leadStatus';
export const REFERRAL_SOURCE = 'referralSource';
export const SPACE = 'spaceLabel';
export const LAST_ACTIVITY = 'lastActivity';
export const DATE_ADDED = 'dateAdded';
export const LAST_UPDATED = 'lastUpdated';
export const EXCHANGE_1031 = 'exchange1031';
export const EXPIRATION_1031 = 'expiration1031';

// Multiple uses of `sortValue` (TODO: interface needs improvement using a class)
// - used to extract the property from the lead, unless Cell has a custom mapping for it
// - must be present to allow per-column sort but may be disabled per component (see table/index)
// - controls client-side project lead sorting (see project/components/leads/list)
// - used for sorting column sent to backend
//
// NOTE: related to the above - to determine sortability, use canSort function.

// TODO: `type` seems unused outside this file - don't expose it
//
// NOTE: If you add a new `sortValue` that's sortable on leads index, make sure backend can handle sorting it
export const COLUMN_DEFINITIONS = {
  [NAME]: { title: 'Name', sortValue: 'displayName' },
  [EMAIL]: { title: 'Email', sortValue: 'emailAddress' },
  [PHONE]: { title: 'Phone', sortValue: 'phoneNumber', disableSort: true },
  [ADDED_BY]: { title: 'Added By' },
  [DOC_ACCESS_LEVEL_FOR_SALE]: { title: 'Sale Doc Access Level' },
  [DOC_ACCESS_LEVEL_FOR_LEASE]: { title: 'Lease Doc Access Level' },
  [ACCOUNT_STATUS]: { title: 'Account Status', sortValue: 'accountStatus' },
  [CA_STATUS]: { title: 'CA Status', sortValue: 'signedCa' },
  [WAITLIST_STATUS]: {
    title: 'Waitlist Status',
    sortValue: 'approvalStatus',
    tooltip: 'The Waitlist Status column only appears if Waitlist Access in Sharing settings is enabled. ' +
    'The status indicates if the lead has logged in and viewed the website, is pending approval, or the lead’s ' +
    'access has been approved, revoked, or blocked.'
  },
  [LEAD_STATUS]: {
    title: 'Lead Status',
    sortValue: 'statusLabel',
    tooltip: 'Lead Status listed is manually updated and the options are configurable within Company Settings ' +
    '> Projects.'
  },
  [LINK_SENT]: { title: 'Link Sent', sortValue: 'oneTimeLinkSentAt', type: 'date' },
  [REFERRAL_SOURCE]: {
    title: 'Referral Source',
    sortValue: 'referralSourceLabel',
    tooltip: 'Referral Source identifies how leads were generated and appears in the Client Report\'s Total Lead Sources card.'
  },
  [COMPANY]: { title: 'Company', sortValue: 'contactCompany' },
  [ROLE_JOB_TITLE]: { title: 'Role / Job Title' },
  [PROPERTY]: { title: 'Property' },
  [SPACE]: { title: 'Space', sortValue: 'listingLabel' },
  [LAST_ACTIVITY]: { title: 'Last Activity', sortValue: 'lastActivityDate' }, // NOTE: "type" is excluded from sort
  [DATE_ADDED]: { title: 'Date Added', sortValue: 'createdAt', type: 'date' },
  [LAST_UPDATED]: { title: 'Last Updated', sortValue: 'updatedAt', type: 'date' },
  [EXCHANGE_1031]: { title: '1031 Exchange', sortValue: 'is1031Profile' },
  [EXPIRATION_1031]: { title: '1031 Expiration', sortValue: 'expirationDate1031', type: 'date' }
};

{
  const canSort = function canSort() {
    return !!this.sortValue && !this.disableSort;
  };

  forEach(COLUMN_DEFINITIONS, (definition) => {
    definition.canSort = canSort;
  });
}

const CONFIGURABLE_COLUMNS_KEYS = reject(Object.keys(COLUMN_DEFINITIONS), column => column === NAME);
export const CONFIGURABLE_COLUMNS = (() => {
  const columnsObject = {};
  CONFIGURABLE_COLUMNS_KEYS.forEach(column => columnsObject[column] = true);

  return columnsObject;
})();

function EmptyCell() {
  return <span>--</span>;
}

function SimpleCell({ content, isDate }) {
  if (!content) return <EmptyCell />;
  if (isDate) return <DateWithHover timestamp={content} />;

  return content;
}
SimpleCell.propTypes = {
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  isDate: PropTypes.bool
};

export default function Cell({ lead, columnName }) {
  let cellContent;

  if (Cell[columnName]) {
    const CellComponent = Cell[columnName];
    cellContent = <CellComponent columnName={columnName} lead={lead} />;
  } else if (columnName === DOC_ACCESS_LEVEL_FOR_LEASE || columnName === DOC_ACCESS_LEVEL_FOR_SALE) {
    const CellComponent = lead.useVaultGates ? DocumentProgress : DocAccessLevelSelectCell;
    cellContent = <CellComponent columnName={columnName} lead={lead}/>;
  } else {
    const { sortValue, type } = COLUMN_DEFINITIONS[columnName];
    cellContent = <SimpleCell content={get(lead, sortValue)} isDate={type === 'date'} />;
  }

  return <td>{cellContent}</td>;
}
Cell.propTypes = {
  columnName: PropTypes.string.isRequired,
  lead: PropTypes.object.isRequired
};

function PhoneCell({ lead }) {
  return <SimpleCell content={formatPhoneNumber(lead.contactPhoneNumber)} />;
}
PhoneCell.propTypes = {
  lead: PropTypes.shape({
    contactPhoneNumber: PropTypes.string
  }).isRequired
};

function CompanyCell({ lead }) {
  if (!lead.contactCompany) return <EmptyCell />;

  const companyName = <strong>{startCase(lead.contactCompany)}</strong>;
  return lead.contactCompanyPath ? <a href={lead.contactCompanyPath}>{companyName}</a> : companyName;
}
CompanyCell.propTypes = {
  lead: PropTypes.shape({
    contactCompany: PropTypes.string,
    contactCompanyPath: PropTypes.string
  }).isRequired
};

// LeadNameCell doesn't seem to be used currenlty. It seems that it's getting overridden in the `Row` component
// with a custom component that handles lead name
function LeadNameCell({ lead }) {
  const name = <strong>{lead.displayName}</strong>;

  return <div className="lead-name" title={lead.displayName}>
    {lead.contactPath ? <a href={lead.contactPath}>{name}</a> : name}
  </div>;
}
LeadNameCell.propTypes = {
  lead: PropTypes.shape({
    displayName: PropTypes.string.isRequired,
    contactPath: PropTypes.string
  }).isRequired
};

function RoleJobTitleCell({ lead }) {
  const content = [
    startCase(get(lead, 'profileUserType')),
    startCase(get(lead, 'leadData.jobTitle'))
  ].filter(n => n.trim().length > 0);

  return <SimpleCell content={compact(content).join(' / ')} />;
}
RoleJobTitleCell.propTypes = {
  lead: PropTypes.shape({
    leadData: PropTypes.shape({ jobTitle: PropTypes.string }),
    profileUserType: PropTypes.string
  }).isRequired
};

function WebStatusCell({ lead }) {
  const caSignatureUrl = lead.caSignatureUrl;

  return (
    <React.Fragment>
      {/* TODO: Why are we hardcoding "Signed CA"? Isn't it set to that value in `lead.webStatus`? */}
      {caSignatureUrl ?
        (<a href={caSignatureUrl} rel="noopener noreferrer" target="_blank">
          <strong>Signed CA</strong>
        </a>) :
        <div>{lead.webStatus}</div>
      }
    </React.Fragment>
  );
}
WebStatusCell.propTypes = {
  lead: PropTypes.shape({
    caSignatureUrl: PropTypes.string,
    webStatus: PropTypes.string
  }).isRequired
};

function CaStatusCell({ lead }) {
  return (
    <div>
      {lead.signedCa &&
        <a href={lead.caSignatureUrl} rel="noopener noreferrer" style={{ color: '#E20067' }} target="_blank">
          <FontAwesomeIcon className="mr-2" icon={faSignature} />
          <strong>Signed CA</strong>
        </a>}
      {!lead.signedCa && <div style={{ color: '#8E93A4' }}>
        <FontAwesomeIcon className="mr-2" icon={faSignatureSlash} />
        <strong>Not Signed</strong>
      </div>}
    </div>
  );
}
CaStatusCell.propTypes = {
  lead: PropTypes.shape({
    caSignatureUrl: PropTypes.string,
    webStatus: PropTypes.string
  }).isRequired
};

function AccountStatusCell({ lead }) {
  return (
    <div>
      {lead.accountStatus === 'Verified' && <div style={{ color: '#1fb37b' }}>
        <FontAwesomeIcon className="mr-2" icon={faCheckCircle} />
        <strong>Verified</strong>
      </div>}
      {lead.accountStatus === 'Not Verified' && <div style={{ color: '#8E93A4' }}>
        <FontAwesomeIcon className="mr-2" icon={faTimesCircle} />
        <strong>{lead.accountStatus}</strong>
      </div>}
    </div>
  );
}
AccountStatusCell.propTypes = {
  lead: PropTypes.shape({
    caSignatureUrl: PropTypes.string,
    webStatus: PropTypes.string
  }).isRequired
};

function LastActivityCell({ lead }) {
  if (!lead.lastActivityDate || !lead.lastActivityType) return <EmptyCell />;

  return (
    <span>
      {getTaskTypeLabel(lead.lastActivityType)} on <DateWithHover timestamp={lead.lastActivityDate} />
    </span>
  );
}
LastActivityCell.propTypes = {
  lead: PropTypes.shape({
    lastActivityDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    lastActivityType: PropTypes.string
  }).isRequired
};

function Exchange1031Cell({ lead }) {
  if (!isBoolean(lead.is1031Profile)) return <EmptyCell />;

  return <React.Fragment>
    {lead.is1031Profile ? 'Yes' : 'No'}
  </React.Fragment>;
}
Exchange1031Cell.propTypes = {
  lead: PropTypes.shape({
    is1031Profile: PropTypes.bool
  }).isRequired
};

function PropertyCell(props) {
  const { lead } = props;
  const propertyInfoTableAddress = () => {
    if (Array.isArray(lead.projectAddress)) {
      return lead.projectAddress.map((line, i) => <div key={i}>{line}</div>);
    } else {
      return <div>{lead.projectAddress}</div>;
    }
  };

  return (
    <InfoTableTooltip
      link={`${lead.projectPath}/project_leads`}
      rows={[{ label: 'Address', value: propertyInfoTableAddress() }]}
      target="_self"
      title={lead.projectName}
    />
  );
}
PropertyCell.propTypes = {
  lead: PropTypes.shape({
    projectAddress: PropTypes.arrayOf(PropTypes.string),
    projectName: PropTypes.string,
    projectPath: PropTypes.string
  }).isRequired
};

Cell[COMPANY] = CompanyCell;
Cell[ADDED_BY] = AddedByCell;
Cell[NAME] = LeadNameCell;
Cell[ROLE_JOB_TITLE] = RoleJobTitleCell;
Cell[ACCOUNT_STATUS] = AccountStatusCell;
Cell[CA_STATUS] = CaStatusCell;
Cell[WAITLIST_STATUS] = WaitlistStatusCell;
Cell[LAST_ACTIVITY] = LastActivityCell;
Cell[EXCHANGE_1031] = Exchange1031Cell;
Cell[PROPERTY] = PropertyCell;
Cell[PHONE] = PhoneCell;
